import React, { memo } from 'react';
import {
  Box, InputAdornment, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';

import type { InterviewRubricItem } from '../../../types/admissions';
import { isScored, onStep } from '../../../utils/admissionAssessment';
import { segmentedSx } from '../tabLayout';

/** Marks up to this many steps show as buttons (0 · 0.5 · 1 · 1.5 · 2). */
const MAX_BUTTONS = 11;

interface Props {
  item: InterviewRubricItem;
  index: number;
  /** Marks as typed, the chosen option, or the note. */
  value: string;
  step: number;
  onChange: (index: number, value: string) => void;
}

/** Is this answer acceptable as entered? Blank is fine only when optional. */
export function answerValid(item: InterviewRubricItem, value: string, step: number): boolean {
  if (isScored(item)) {
    if (value.trim() === '') return false;
    const n = Number(value);
    return Number.isFinite(n) && n >= 0 && n <= item.maxMarks && onStep(n, step);
  }
  if (!value.trim()) return !item.required;
  return item.kind !== 'choice' || (item.options || []).includes(value);
}


/**
 * One question on a round's sheet, answered the way it was set up: marks
 * (buttons for small maxima, a field otherwise), one of the options, or a
 * free-text note.
 */
const RubricAnswerField = memo<Props>(({ item, index, value, step, onChange }) => {
  const kind = item.kind ?? 'marks';
  const label = (
    <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
      {item.criterion}
      {kind !== 'marks' && item.required && <Box component="span" sx={{ color: 'error.main' }}> *</Box>}
    </Typography>
  );

  if (kind === 'note') {
    return (
      <Box>
        {label}
        <TextField size="small" fullWidth multiline minRows={1} value={value} onChange={(e) => onChange(index, e.target.value)} sx={{ mt: 0.75 }} />
      </Box>
    );
  }

  if (kind === 'choice') {
    return (
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
        {label}
        <ToggleButtonGroup
          size="small"
          exclusive
          value={value || null}
          onChange={(_, v) => onChange(index, v ?? '')}
          aria-label={item.criterion}
          sx={segmentedSx}
        >
          {(item.options || []).map(o => <ToggleButton key={o} value={o}>{o}</ToggleButton>)}
        </ToggleButtonGroup>
      </Stack>
    );
  }

  const steps = Math.round(item.maxMarks / step);
  const invalid = value.trim() !== '' && !answerValid(item, value, step);
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
      {label}
      {steps < MAX_BUTTONS && onStep(item.maxMarks, step) ? (
        <ToggleButtonGroup
          size="small"
          exclusive
          value={value === '' ? null : Number(value)}
          onChange={(_, v) => onChange(index, v == null ? '' : String(v))}
          aria-label={item.criterion}
          sx={segmentedSx}
        >
          {Array.from({ length: steps + 1 }, (_, i) => i * step).map(m => (
            <ToggleButton key={m} value={m}>{m}</ToggleButton>
          ))}
        </ToggleButtonGroup>
      ) : (
        <TextField
          size="small"
          value={value}
          onChange={(e) => onChange(index, e.target.value.replace(/[^\d.]/g, ''))}
          error={invalid}
          helperText={invalid && value.trim() !== '' && Number(value) <= item.maxMarks ? `Steps of ${step}` : undefined}
          inputProps={{ inputMode: 'decimal', style: { textAlign: 'right' }, 'aria-label': item.criterion }}
          InputProps={{ endAdornment: <InputAdornment position="end">/ {item.maxMarks}</InputAdornment> }}
          sx={{ width: 150 }}
        />
      )}
    </Stack>
  );
});

RubricAnswerField.displayName = 'RubricAnswerField';

export default RubricAnswerField;
